import bookOpenSVG from '../assets/img/bookOpen.svg';
import typeSVG from '../assets/img/type.svg';
import lineHeightSVG from '../assets/img/lineHeight.svg';
import letterSpacingSVG from '../assets/img/letterSpacing.svg';
import wordSpacingSVG from '../assets/img/wordSpacing.svg';

window.VAE = window.VAE || {};

VAE.Text = (function () {
  'use strict';

  const TARGET_SELECTORS =
    'p, li, span, td, th, a, h1, h2, h3, h4, h5, h6, div:not([id*="vae"]):not(:has(> script)):not(:has(> style)), label, dt, dd, figcaption, blockquote, pre';
  const EXCLUDED_SELECTORS =
    'script, style, noscript, button, input, textarea, select, option, nav, [role="button"], [role="navigation"], [role="menubar"], #vae-widget-container, #vae-widget-container *, [data-vae-ignore-text-size]';
  const ORIGINAL_SIZE_ATTR = 'data-vae-original-font-size';
  const ORIGINAL_LINE_HEIGHT_ATTR = 'data-vae-original-line-height';
  const ORIGINAL_FONT_FAMILY_ATTR = 'data-vae-original-font-family';
  const READABILITY_CONTAINER_ID = 'vae-readability-container';

  const LETTER_SPACING_EM_FACTOR = 0.002;
  const WORD_SPACING_EM_FACTOR = 0.005;

  const FONT_OPTIONS = [
    { value: 'default', text: 'Default' },
    { value: 'sans-serif', text: 'Sans-Serif' },
    { value: 'serif', text: 'Serif' },
    { value: 'monospace', text: 'Monospace' },
    { value: 'readable-dyslexia', text: 'Readable (Dyslexia)' },
  ];

  const FONT_FAMILY_MAP = {
    default: null,
    'sans-serif': 'sans-serif',
    serif: 'serif',
    monospace: 'monospace',
    'readable-dyslexia':
      '"OpenDyslexic", "Atkinson Hyperlegible", Verdana, Arial, sans-serif',
  };

  let observer = null;
  let originalContent = null;
  let subscriptionActive = false;
  let controlSetters = {};

  function initStateManager() {
    if (subscriptionActive) return;

    console.log('VAE.Text: Subscribing to state changes');

    captureOriginalValues();

    let currentState = null;
    try {
      currentState = VAE.StateManager.getState('text');
      console.log('VAE.Text: Current state from StateManager:', currentState);
    } catch (e) {
      console.error('VAE.Text: Error getting state from StateManager:', e);
    }

    VAE.StateManager.subscribe('text', handleStateUpdate);
    subscriptionActive = true;
  }

  function captureOriginalValues() {
    console.log('VAE.Text: Capturing original values for all elements');

    if (document.documentElement.hasAttribute('data-vae-originals-captured')) {
      console.log('VAE.Text: Original values already captured');
      return;
    }

    try {
      const elements = document.querySelectorAll(TARGET_SELECTORS);
      elements.forEach((element) => {
        if (!element.matches(EXCLUDED_SELECTORS)) {
          if (!element.hasAttribute(ORIGINAL_SIZE_ATTR)) {
            const computedStyle = window.getComputedStyle(element);
            const fontSize = computedStyle.fontSize;
            if (fontSize && fontSize.endsWith('px')) {
              const sizePx = parseFloat(fontSize);
              if (!isNaN(sizePx) && sizePx > 0) {
                element.setAttribute(ORIGINAL_SIZE_ATTR, sizePx);
              }
            }
          }

          if (!element.hasAttribute(ORIGINAL_LINE_HEIGHT_ATTR)) {
            const computedStyle = window.getComputedStyle(element);
            const lineHeight = computedStyle.lineHeight;

            if (lineHeight === 'normal') {
              element.setAttribute(ORIGINAL_LINE_HEIGHT_ATTR, 'normal');
            } else if (lineHeight.endsWith('px')) {
              const value = parseFloat(lineHeight);
              if (!isNaN(value)) {
                element.setAttribute(ORIGINAL_LINE_HEIGHT_ATTR, value);
              }
            } else if (!isNaN(parseFloat(lineHeight))) {
              const value = parseFloat(lineHeight);
              element.setAttribute(ORIGINAL_LINE_HEIGHT_ATTR, value);
            } else {
              element.setAttribute(ORIGINAL_LINE_HEIGHT_ATTR, 'unhandled');
            }
          }
        }
      });

      document.documentElement.setAttribute(
        'data-vae-originals-captured',
        'true'
      );
      console.log('VAE.Text: Original values captured for all elements');
    } catch (e) {
      console.error('VAE.Text: Error capturing original values:', e);
    }
  }

  initStateManager();

  function storeOriginalLineHeight(element) {
    if (element.hasAttribute(ORIGINAL_LINE_HEIGHT_ATTR)) {
      return;
    }

    try {
      const computedStyle = window.getComputedStyle(element);
      const lineHeight = computedStyle.lineHeight;
      element.setAttribute(ORIGINAL_LINE_HEIGHT_ATTR, lineHeight);
    } catch (e) {
      console.warn(
        'VAE.Text: Could not get computed line height for',
        element,
        e
      );
      element.setAttribute(ORIGINAL_LINE_HEIGHT_ATTR, 'unhandled');
    }
  }

  function applyLineHeightToElement(element, percentage) {
    if (element.matches(EXCLUDED_SELECTORS)) {
      return;
    }

    storeOriginalLineHeight(element);
    const originalString = element.getAttribute(ORIGINAL_LINE_HEIGHT_ATTR);

    if (!originalString || originalString === 'unhandled') {
      return;
    }

    const multiplier = percentage / 100;

    if (originalString === 'normal') {
      const newHeight = 1.2 * multiplier;
      element.style.setProperty('line-height', newHeight, 'important');
    } else if (originalString.endsWith('px')) {
      const originalValue = parseFloat(originalString);
      if (!isNaN(originalValue)) {
        const newHeight = originalValue * multiplier;
        element.style.setProperty('line-height', `${newHeight}px`, 'important');
      }
    } else if (!isNaN(parseFloat(originalString))) {
      const originalValue = parseFloat(originalString);
      const newHeight = originalValue * multiplier;
      element.style.setProperty('line-height', newHeight, 'important');
    }
  }

  function handleStateUpdate(textState) {
    if (!textState) {
      console.warn('VAE.Text: Invalid state received in handleStateUpdate');
      return;
    }

    console.log('VAE.Text: Handling text state update', textState);

    const DEFAULT_VALUES = {
      fontSize: 1,
      lineHeight: 1,
      letterSpacing: 0,
      wordSpacing: 0,
      fontFamily: 'default',
      readabilityMode: false
    };

    if (textState.fontSize !== DEFAULT_VALUES.fontSize) {
      applyTextSizeFromState(textState);
    }
    if (textState.lineHeight !== DEFAULT_VALUES.lineHeight) {
      applyLineHeightFromState(textState);
    }
    if (textState.letterSpacing !== DEFAULT_VALUES.letterSpacing) {
      applyLetterSpacingFromState(textState);
    }
    if (textState.wordSpacing !== DEFAULT_VALUES.wordSpacing) {
      applyWordSpacingFromState(textState);
    }
    if (textState.fontFamily !== DEFAULT_VALUES.fontFamily) {
      applyFontFamilyFromState(textState);
    }

    const isCurrentlyReadability =
      document.getElementById(READABILITY_CONTAINER_ID) !== null;
    if (textState.readabilityMode === true && !isCurrentlyReadability) {
      console.log('VAE.Text: Enabling readability mode from state update');
      enableReadabilityMode();
    } else if (textState.readabilityMode === false && isCurrentlyReadability) {
      console.log('VAE.Text: Disabling readability mode from state update');
      disableReadabilityMode();
    }

    try {
      console.log('VAE.Text: Updating UI controls with state', textState);

      if (controlSetters.readabilityMode) {
        controlSetters.readabilityMode(Boolean(textState.readabilityMode));
      }

      if (controlSetters.fontFamily) {
        controlSetters.fontFamily(textState.fontFamily || 'default');
      }
      if (controlSetters.fontSize) {
        controlSetters.fontSize(
          textState.fontSize ? textState.fontSize * 100 : 100
        );
      }
      if (controlSetters.lineHeight) {
        controlSetters.lineHeight(
          textState.lineHeight ? textState.lineHeight * 100 : 100
        );
      }
      if (controlSetters.letterSpacing) {
        controlSetters.letterSpacing(textState.letterSpacing || 0);
      }
      if (controlSetters.wordSpacing) {
        controlSetters.wordSpacing(textState.wordSpacing || 0);
      }
    } catch (e) {
      console.error('VAE.Text: Error updating UI controls with state:', e);
    }
  }

  function applyTextSizeFromState(state) {
    if (!state.hasOwnProperty('fontSize')) return false;

    const newSize = state.fontSize || 1;
    console.log('VAE.Text: Applying font size', newSize * 100, '% from state');
    applyToAllTargetElements((element) =>
      applyTextSizeToElement(element, newSize)
    );
    return true;
  }

  function applyLineHeightFromState(state) {
    if (!state.hasOwnProperty('lineHeight')) return false;

    const newLineHeight = state.lineHeight ? state.lineHeight * 100 : 100;
    console.log(
      'VAE.Text: Applying line height',
      newLineHeight,
      '% from state'
    );
    applyToAllTargetElements((element) =>
      applyLineHeightToElement(element, newLineHeight)
    );
    return true;
  }

  function applyLetterSpacingFromState(state) {
    if (!state.hasOwnProperty('letterSpacing')) return false;

    const newLetterSpacing = state.letterSpacing || 0;
    console.log(
      'VAE.Text: Applying letter spacing',
      newLetterSpacing,
      'from state'
    );
    applyToAllTargetElements((element) =>
      applyLetterSpacingToElement(element, newLetterSpacing)
    );
    return true;
  }

  function applyWordSpacingFromState(state) {
    if (!state.hasOwnProperty('wordSpacing')) return false;

    const newWordSpacing = state.wordSpacing || 0;
    console.log(
      'VAE.Text: Applying word spacing',
      newWordSpacing,
      'from state'
    );
    applyToAllTargetElements((element) =>
      applyWordSpacingToElement(element, newWordSpacing)
    );
    return true;
  }

  function applyFontFamilyFromState(state) {
    if (!state.hasOwnProperty('fontFamily')) return false;

    const newFontFamily = state.fontFamily || 'default';
    console.log('VAE.Text: Applying font family', newFontFamily, 'from state');

    storeOriginalFontFamily();
    applyToAllTargetElements((element) =>
      applyFontFamilyToElement(element, newFontFamily)
    );
    document.documentElement.classList.toggle(
      'vae-dyslexia-font',
      newFontFamily === 'readable-dyslexia'
    );

    return true;
  }

  function areSettingsDefault() {
    try {
      const currentState = VAE.StateManager.getState('text');
      const DEFAULT_TEXT_STATE = {
        fontSize: 1,
        lineHeight: 1,
        letterSpacing: 0,
        wordSpacing: 0,
        fontFamily: 'default',
        readabilityMode: false,
      };

      for (const key in DEFAULT_TEXT_STATE) {
        if (
          currentState.hasOwnProperty(key) &&
          currentState[key] !== DEFAULT_TEXT_STATE[key]
        ) {
          return false;
        }
      }

      return true;
    } catch (e) {
      console.error("VAE.Text: Error checking if settings are default:", e);
      throw e;
    }
  }

  function applyToAllTargetElements(action) {
    const elements = document.querySelectorAll(TARGET_SELECTORS);
    elements.forEach((element) => {
      action(element);
    });
  }

  function storeOriginalFontSize(element) {
    if (!element.hasAttribute(ORIGINAL_SIZE_ATTR)) {
      try {
        const computedStyle = window.getComputedStyle(element);
        const fontSize = computedStyle.fontSize;
        if (fontSize && fontSize.endsWith('px')) {
          const sizePx = parseFloat(fontSize);
          if (!isNaN(sizePx) && sizePx > 0) {
            element.setAttribute(ORIGINAL_SIZE_ATTR, sizePx);
            return sizePx;
          }
        }
      } catch (e) {
        console.warn(
          'VAE.Text: Could not get computed font size for',
          element,
          e
        );
      }
    }
    const storedSize = element.getAttribute(ORIGINAL_SIZE_ATTR);
    return storedSize ? parseFloat(storedSize) : null;
  }

  function storeOriginalFontFamily() {
    if (!document.body.hasAttribute(ORIGINAL_FONT_FAMILY_ATTR)) {
      try {
        const computedStyle = window.getComputedStyle(document.body);
        const fontFamily = computedStyle.fontFamily;
        document.body.setAttribute(
          ORIGINAL_FONT_FAMILY_ATTR,
          fontFamily || 'default'
        );
      } catch (e) {
        console.warn('VAE.Text: Could not get computed body font family', e);
        document.body.setAttribute(ORIGINAL_FONT_FAMILY_ATTR, 'default');
      }
    }
  }

  function applyTextSizeToElement(element, multiplier) {
    if (element.matches(EXCLUDED_SELECTORS)) {
      return;
    }
    const originalSizePx = storeOriginalFontSize(element);
    if (originalSizePx !== null && originalSizePx > 0) {
      const newSize = originalSizePx * multiplier;
      element.style.setProperty('font-size', `${newSize}px`, 'important');
    }
  }

  function applyFontFamilyToElement(element, fontKey) {
    if (element.matches(EXCLUDED_SELECTORS)) {
      return;
    }
    const fontFamilyValue = FONT_FAMILY_MAP[fontKey];
    if (fontFamilyValue === null) {
      element.style.removeProperty('font-family');
    } else {
      element.style.setProperty('font-family', fontFamilyValue, 'important');
    }
  }

  function applyLetterSpacingToElement(element, percentage) {
    if (element.matches(EXCLUDED_SELECTORS)) {
      return;
    }
    const actualLetterSpacing = LETTER_SPACING_EM_FACTOR * percentage;
    if (percentage === 0) {
      element.style.removeProperty('letter-spacing');
    } else {
      element.style.setProperty(
        'letter-spacing',
        `${actualLetterSpacing}em`,
        'important'
      );
    }
  }

  function applyWordSpacingToElement(element, percentage) {
    if (element.matches(EXCLUDED_SELECTORS)) {
      return;
    }
    const actualWordSpacing = WORD_SPACING_EM_FACTOR * percentage;
    if (percentage === 0) {
      element.style.removeProperty('word-spacing');
    } else {
      element.style.setProperty(
        'word-spacing',
        `${actualWordSpacing}em`,
        'important'
      );
    }
  }

  function applyFontSize(percentage) {
    const multiplier = percentage / 100;

    initStateManager();

    console.log('VAE.Text: Setting font size to', percentage, '%');
    VAE.StateManager.updateState('text', { fontSize: multiplier });
  }

  function applyLineHeight(percentage) {
    initStateManager();

    console.log('VAE.Text: Setting line height to', percentage, '%');
    VAE.StateManager.updateState('text', { lineHeight: percentage / 100 });
  }

  function applyLetterSpacing(percentage) {
    initStateManager();

    console.log('VAE.Text: Setting letter spacing to', percentage);
    VAE.StateManager.updateState('text', { letterSpacing: percentage });
  }

  function applyWordSpacing(percentage) {
    initStateManager();

    console.log('VAE.Text: Setting word spacing to', percentage);
    VAE.StateManager.updateState('text', { wordSpacing: percentage });
  }

  function applyFontFamily(fontKey) {
    initStateManager();

    console.log('VAE.Text: Setting font family to', fontKey);
    VAE.StateManager.updateState('text', { fontFamily: fontKey });
  }

  function handleMutations(mutations) {
    if (Boolean(VAE.StateManager.getState('text').readabilityMode)) return;

    mutations.forEach((mutation) => {
      if (mutation.type === 'childList') {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const currentState = VAE.StateManager.getState('text');
            if (
              node.matches(TARGET_SELECTORS) &&
              !node.matches(EXCLUDED_SELECTORS)
            ) {
              applyTextSizeToElement(node, currentState.fontSize);
              applyLineHeightToElement(node, currentState.lineHeight * 100);
              applyFontFamilyToElement(node, currentState.fontFamily);
              applyLetterSpacingToElement(node, currentState.letterSpacing);
              applyWordSpacingToElement(node, currentState.wordSpacing);
            }
            node.querySelectorAll(TARGET_SELECTORS).forEach((element) => {
              applyTextSizeToElement(element, currentState.fontSize);
              applyLineHeightToElement(element, currentState.lineHeight * 100);
              applyFontFamilyToElement(element, currentState.fontFamily);
              applyLetterSpacingToElement(element, currentState.letterSpacing);
              applyWordSpacingToElement(element, currentState.wordSpacing);
            });
          }
        });
      }
    });
  }

  function checkObserverState() {
    if (!observer && !areSettingsDefault()) {
      startObserver();
    } else if (observer && areSettingsDefault()) {
      stopObserver();
    }
  }

  function startObserver() {
    if (observer) return;
    console.log('VAE.Text: Starting MutationObserver for text styles');
    observer = new MutationObserver(handleMutations);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  function stopObserver() {
    if (observer) {
      console.log('VAE.Text: Stopping MutationObserver');
      observer.disconnect();
      observer = null;
    }
  }

  function enableReadabilityMode() {
    console.log('VAE.Text: Enabling readability mode');

    if (!originalContent) {
      originalContent = document.documentElement.cloneNode(true);
    }
    
    const readabilityUrl = 'https://cdn.jsdelivr.net/npm/@mozilla/readability@0.4.4/Readability.min.js';
    VAE.loadExternalScript(readabilityUrl)
      .then(() => {
        try {
          const docClone = document.cloneNode(true);
          const reader = new window.Readability(docClone);
          const article = reader.parse();

          if (!article) {
            console.error(
              'VAE.Text: Readability could not parse the page content'
            );
            return;
          }

          let container = document.getElementById(READABILITY_CONTAINER_ID);
          if (!container) {
            container = document.createElement('div');
            container.id = READABILITY_CONTAINER_ID;
            container.style.cssText = `
              position: absolute;
              top: 0;
              left: 0;
              width: 100%;
              min-height: 100vh;
              background-color: white;
              padding: 2rem;
              box-sizing: border-box;
              z-index: 999999;
              overflow-y: auto;
              max-width: 800px;
              margin: 0 auto;
              right: 0;
            `;
          }

          container.innerHTML = `
            <h1>${article.title}</h1>
            <div>${article.content}</div>
          `;

          const style = document.createElement('style');
          style.id = 'vae-readability-styles';
          const currentFontFamily = VAE.StateManager.getState('text').fontFamily;
          style.textContent = `
            #${READABILITY_CONTAINER_ID} {
              font-family: ${FONT_FAMILY_MAP[currentFontFamily] || 'Georgia, serif'};
              font-size: 1.2rem;
              line-height: 1.6;
              color: #333;
            }
            #${READABILITY_CONTAINER_ID} h1 {
              font-size: 2rem;
              margin-bottom: 1.5rem;
            }
            #${READABILITY_CONTAINER_ID} p {
              margin-bottom: 1rem;
            }
            #${READABILITY_CONTAINER_ID} a {
              color: #0066cc;
            }
            #${READABILITY_CONTAINER_ID} img {
              max-width: 100%;
              height: auto;
              margin: 1rem 0;
            }
          `;

          document.head.appendChild(style);
          document.body.appendChild(container);

          Array.from(document.body.children).forEach((child) => {
            if (child !== container && child.id !== 'vae-widget-container') {
              child.style.display = 'none';
            }
          });

          const currentState = VAE.StateManager.getState('text');

          if (currentState.fontSize !== 1) {
            applyToAllTargetElements((element) => {
              if (element.closest(`#${READABILITY_CONTAINER_ID}`)) {
                applyTextSizeToElement(element, currentState.fontSize);
              }
            });
          }

          if (currentState.lineHeight !== 1) {
            applyToAllTargetElements((element) => {
              if (element.closest(`#${READABILITY_CONTAINER_ID}`)) {
                applyLineHeightToElement(element, currentState.lineHeight * 100);
              }
            });
          }

          if (currentState.letterSpacing !== 0) {
            applyToAllTargetElements((element) => {
              if (element.closest(`#${READABILITY_CONTAINER_ID}`)) {
                applyLetterSpacingToElement(element, currentState.letterSpacing);
              }
            });
          }

          if (currentState.wordSpacing !== 0) {
            applyToAllTargetElements((element) => {
              if (element.closest(`#${READABILITY_CONTAINER_ID}`)) {
                applyWordSpacingToElement(element, currentState.wordSpacing);
              }
            });
          }

          checkObserverState();
          console.log('VAE.Text: Readability mode enabled successfully');
        } catch (err) {
          console.error('VAE.Text: Error applying readability mode', err);
        }
      })
      .catch((err) => {
        console.error('VAE.Text: Failed to enable readability mode', err);
      });
  }

  function disableReadabilityMode() {
    console.log('VAE.Text: Disabling readability mode');

    const container = document.getElementById(READABILITY_CONTAINER_ID);
    if (container) {
      document.body.removeChild(container);
    }

    const style = document.getElementById('vae-readability-styles');
    if (style) {
      document.head.removeChild(style);
    }

    Array.from(document.body.children).forEach((child) => {
      if (child.id !== 'vae-widget-container') {
        child.style.display = '';
      }
    });

    checkObserverState();
    console.log('VAE.Text: Readability mode disabled successfully');
  }

  function toggleReadabilityMode(enabled) {
    initStateManager();

    console.log(
      `VAE.Text: ${
        enabled ? 'Enabling' : 'Disabling'
      } readability mode via UI toggle`
    );
    VAE.StateManager.updateState('text', { readabilityMode: enabled });
  }

  function renderSectionContent(container) {
    container.innerHTML = '';
    controlSetters = {};

    try {
      const currentState = VAE.StateManager.getState('text');
      console.log('VAE.Text: Rendering tab content with state:', currentState);

      const createSwitchToggle = VAE.SharedUI.createSwitchToggle;
      const createDropdown = VAE.SharedUI.createDropdown;
      const createNumericStepper = VAE.SharedUI.createNumericStepper;

      const readingModeControl = createSwitchToggle({
        label: 'Reading Mode',
        icon: bookOpenSVG,
        id: 'vae-readability-switch',
        initialChecked: Boolean(currentState?.readabilityMode),
        onChange: toggleReadabilityMode,
      });
      container.appendChild(readingModeControl.container);
      controlSetters.readabilityMode = readingModeControl.setValue;

      const textSettingsContainer = document.createElement('div');
      textSettingsContainer.className = 'vae-text-settings-container';

      const fontDropdownResult = createDropdown({
        label: 'Schriftart',
        icon: typeSVG,
        id: 'vae-font-family-select',
        options: FONT_OPTIONS.map((opt) => ({
          ...opt,
          text: opt.value === 'default' ? 'Standard' : opt.text,
        })),
        initialValue: currentState?.fontFamily || 'default',
        onChange: applyFontFamily,
      });
      controlSetters.fontFamily = fontDropdownResult.setValue;
      textSettingsContainer.appendChild(fontDropdownResult.container);

      const fontSizeStepperResult = createNumericStepper({
        label: 'Schriftgröße',
        icon: typeSVG,
        initialValue: currentState?.fontSize
          ? currentState.fontSize * 100
          : 100,
        minValue: 50,
        maxValue: 200,
        step: 10,
        unit: '%',
        onChange: applyFontSize,
        controlName: 'font-size',
      });
      controlSetters.fontSize = fontSizeStepperResult.setValue;
      textSettingsContainer.appendChild(fontSizeStepperResult.container);

      const lineHeightStepperResult = createNumericStepper({
        label: 'Zeilenhöhe',
        icon: lineHeightSVG,
        initialValue: currentState?.lineHeight
          ? currentState.lineHeight * 100
          : 100,
        minValue: 70,
        maxValue: 200,
        step: 10,
        unit: '%',
        onChange: applyLineHeight,
        controlName: 'line-height',
      });
      controlSetters.lineHeight = lineHeightStepperResult.setValue;
      textSettingsContainer.appendChild(lineHeightStepperResult.container);

      const letterSpacingStepperResult = createNumericStepper({
        label: 'Buchstabenabstand',
        icon: letterSpacingSVG,
        initialValue: currentState?.letterSpacing || 0,
        minValue: -50,
        maxValue: 50,
        step: 5,
        unit: '',
        onChange: applyLetterSpacing,
        controlName: 'letter-spacing',
      });
      controlSetters.letterSpacing = letterSpacingStepperResult.setValue;
      textSettingsContainer.appendChild(letterSpacingStepperResult.container);

      const wordSpacingStepperResult = createNumericStepper({
        label: 'Wortabstand',
        icon: wordSpacingSVG,
        initialValue: currentState?.wordSpacing || 0,
        minValue: 0,
        maxValue: 100,
        step: 10,
        unit: '',
        onChange: applyWordSpacing,
        controlName: 'word-spacing',
      });
      controlSetters.wordSpacing = wordSpacingStepperResult.setValue;
      textSettingsContainer.appendChild(wordSpacingStepperResult.container);

      container.appendChild(textSettingsContainer);

      console.log(
        'VAE.Text: Tab content rendered successfully with current state'
      );
    } catch (e) {
      console.error('VAE.Text: Error rendering tab content:', e);
      container.appendChild(
        document.createTextNode(
          'Error loading text settings. Please try refreshing the page.'
        )
      );
    }
  }

  function getCurrentState() {
    try {
      const stateManagerValues = VAE.StateManager.getState('text');
      if (stateManagerValues) {
        return { ...stateManagerValues };
      }
      return stateManagerValues;
    } catch (e) {
      console.error('VAE.Text: Error getting current state:', e);
      throw e;
    }
  }

  function _init() {
    console.log('VAE.Text: Initializing module');

    const initPromise = new Promise((resolve) => {
      function checkStateManager() {
        if (typeof VAE.StateManager !== 'undefined') {
          console.log('VAE.Text: State manager found, initializing');
          initStateManager();
          resolve();
        } else {
          console.log('VAE.Text: Waiting for state manager');
          setTimeout(checkStateManager, 50);
        }
      }

      checkStateManager();
    });

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        console.log('VAE.Text: DOM ready, applying settings');
        try {
          const currentState = VAE.StateManager.getState('text');
          if (currentState) {
            handleStateUpdate(currentState);
          }
        } catch (e) {
          console.error('VAE.Text: Error applying settings on DOM ready:', e);
        }
      });
    } else {
      console.log('VAE.Text: DOM already ready, applying settings');
      initPromise.then(() => {});
    }

    return true;
  }

  _init();

  return {
    renderSectionContent,
    getCurrentState,
  };
})();